import React, { useState, useEffect } from "react";
import styles from "./styles.module.scss";
import cx from "classnames";
import PropTypes from "prop-types";
import DecimalInput from "../DecimalInput";
import { ScreenReaderLabel } from "@ww-digital/web-foundations-react/modules";
import FormErrorMessage from "../../FormErrorMessage";
import isOutOfBounds from "../../../utils/isOutOfBounds";
import t from "../../../utils/translate";
import useEvent from "../../../utils/useEvent";
import { capitalize } from "lodash";

function SingleHeightInput({
  name,
  market,
  label = {
    placeholder: "",
    appended: ""
  },
  mask = "",
  value,
  onChange,
  min,
  max,
  labelClassName,
  errorClassName,
  fieldsetWrapperClassName,
  capitalizeLabel,
  isShortUnit = false
}) {
  const [hasError, setError] = useState(false);

  const isHeightInvalid = height =>
    height === null || isOutOfBounds(height, min, max);

  const handleChange = (_, value) => {
    const isInvalid = isHeightInvalid(value);
    setError(isInvalid);
    onChange(isInvalid ? null : value);
  };

  const { listen } = useEvent("SettingsValidation");
  useEffect(() => {
    return listen(() => {
      setError(value === null || isOutOfBounds(value, min, max));
    });
  }, [listen, min, max, value]);

  return (
    <>
      <div
        className={cx(
          styles.singleInputContainer,
          styles.appendedLabel,
          fieldsetWrapperClassName
        )}
      >
        <div className={styles.metric}>
          <DecimalInput
            id={name}
            aria-required={true}
            market={market}
            mask={mask}
            error={hasError}
            defaultValue={value ?? ""}
            onChange={handleChange}
          />
          <label
            className={cx(styles.tailingLabel, labelClassName)}
            htmlFor={name}
          >
            {isShortUnit
              ? label.appendedShort
              : capitalizeLabel
                ? capitalize(label.appended)
                : label.appended}
          </label>
          {hasError && (
            <ScreenReaderLabel role="alert">
              Height input of {value} must be between {min} and {max}
            </ScreenReaderLabel>
          )}
        </div>
      </div>
      {hasError && (
        <FormErrorMessage
          role="alert"
          data-test="height-error"
          className={errorClassName}
        >
          {t("HEIGHT_INVALID")}
        </FormErrorMessage>
      )}
    </>
  );
}

SingleHeightInput.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.shape({
    placeholder: PropTypes.string,
    appended: PropTypes.string
  }),
  min: PropTypes.number,
  max: PropTypes.number,
  market: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

export default SingleHeightInput;
